/**
 * Agent Hub landing — `/agent-hub`. Breadcrumb: Home › Agent Hub.
 *
 * Four domain cards (Maritime Card). Ship Management opens the prioritised agent overview;
 * Finance, QHSE and Crewing open their placeholder pages until those hubs ship.
 *
 * The link is on the card's heading, as Maritime's Card asks; CSS stretches its hit area over
 * the whole card, so the card is still one click target.
 */

import { Link } from 'react-router-dom';
import { Banknote, ShieldCheck, Ship, UserRound } from 'lucide-react';
import { Card, Heading, Text } from '@syia-ai/maritimeai-ui';
import SimpleLayout from '@/components/SimpleLayout';

const SUBTITLE = 'Unified management for agents and the sub-agents that power them.';

// `tone` picks the tile colour (shell.css). A "\n" in a description is a line break the
// design asks for.
const DOMAINS = [
  {
    id: 'ship-management',
    title: 'Ship Management',
    href: '/agent-hub/ship-management',
    icon: Ship,
    tone: 'sky',
    description:
      'Oversee vessel operations, maintenance schedules, and technical performance across the fleet.\nA single view into every ship\'s operational health and readiness.',
  },
  {
    id: 'finance',
    title: 'Finance',
    href: '/agent-hub/finance',
    icon: Banknote,
    tone: 'green',
    description:
      'Track budgets, invoices, and financial performance across vessels and voyages. Get real-time visibility into costs, revenue, and financial health.',
  },
  {
    id: 'qhse',
    title: 'QHSE',
    href: '/agent-hub/qhse',
    icon: ShieldCheck,
    tone: 'gold',
    description:
      'Monitor quality, health, safety, and environmental compliance across operations. Stay audit-ready with real-time incident tracking and risk visibility.',
  },
  {
    id: 'crewing',
    title: 'Crewing',
    href: '/agent-hub/crewing',
    icon: UserRound,
    tone: 'navy',
    description:
      'Manage crew assignments, certifications, and rotations across the fleet. A complete view of crew availability, documentation, and deployment status.',
  },
] as const;

export default function AgentHub() {
  return (
    <SimpleLayout
      currentPath="/agent-hub"
      crumbs={[{ label: 'Agent Hub' }]}
      subtitle={SUBTITLE}
      headerTitle="Agent Hub"
    >
      <ul className="shell-domains">
        {DOMAINS.map((domain) => {
          const Icon = domain.icon;
          return (
            <Card key={domain.id} as="li" variant="outlined" padding="none" className="shell-domain">
              <div className="shell-domain__head">
                <span className={`shell-domain__icon shell-domain__icon--${domain.tone}`} aria-hidden>
                  <Icon />
                </span>
                <Heading level={2} size="sm" className="shell-domain__title">
                  <Link to={domain.href} className="shell-domain__link">
                    {domain.title}
                  </Link>
                </Heading>
              </div>
              <Text className="shell-domain__desc">{domain.description}</Text>
            </Card>
          );
        })}
      </ul>
    </SimpleLayout>
  );
}
